import React, { useState, useRef, useEffect } from 'react';
import { Loader2, Download, Link2, CheckCircle2, RotateCcw, XCircle } from 'lucide-react';
import { authFetch, postJson, downloadFile } from './utileApi';

// Télécharger depuis un lien YouTube / Facebook : la vidéo en MP4, ou (audio=true) le son seulement en MP3.
// Le serveur récupère le média, le client le télécharge ensuite.
export default function DownloadTool({ status, reload, audio = false }) {
  const left = audio ? status?.dlmp3_left : status?.dl_left;
  const limit = audio ? status?.dlmp3_limit : status?.dl_limit;
  const [url, setUrl] = useState('');
  const [job, setJob] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const stop = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  useEffect(() => stop, []);

  const watch = (j) => {
    stop(); setJob(j);
    if (j.status === 'done' || j.status === 'error') return;
    pollRef.current = setInterval(async () => {
      try {
        const r = await authFetch(`/api/utile/jobs/${j.id}`);
        const d = await r.json();
        if (!d.success) { stop(); setError(d.message || 'Tâche introuvable'); setJob(null); return; }
        setJob(d.job);
        if (d.job.status === 'done' || d.job.status === 'error') { stop(); reload(); }
      } catch (_) { /* coupure réseau passagère */ }
    }, 2000);
  };

  const start = async () => {
    setError(''); setBusy(true);
    const { status: st, data } = await postJson(audio ? '/api/utile/download-mp3' : '/api/utile/download', { url });
    setBusy(false);
    if (st === 200 && data.success) { watch(data.job); reload(); }
    else setError(data.message || 'Téléchargement impossible. Réessayez.');
  };

  const save = async () => {
    try { await downloadFile(`/api/utile/jobs/${job.id}/file`, job.file_name || (audio ? 'son.mp3' : 'video.mp4')); }
    catch (e) { setError(e.message); }
  };

  const reset = () => { stop(); setJob(null); setUrl(''); setError(''); };
  const running = job && (job.status === 'queued' || job.status === 'running');

  return (
    <div className="space-y-4">
      {!job && (
        <>
          <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
            <label className="text-sm text-blue-200 mb-2 flex items-center gap-2"><Link2 className="w-4 h-4" /> Lien de la vidéo (Facebook ou YouTube)</label>
            <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.facebook.com/…  ou  https://youtu.be/…"
              className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-sm placeholder-gray-500 focus:outline-none focus:border-sky-400" />
            <p className="text-xs text-gray-400 mt-2">{audio ? 'Vous recevez uniquement le son, en MP3 (pas de vidéo). ' : ''}Seules les vidéos publiques sont possibles (maximum 60 minutes). Utilisez cet outil uniquement pour des contenus dont vous avez les droits ou l'autorisation.</p>
            {status && !status.youtube_available && (
              <p className="text-xs text-amber-300 mt-2">YouTube est momentanément indisponible. Les liens Facebook fonctionnent.</p>
            )}
          </div>
          {error && <p className="text-rose-300 text-sm bg-rose-500/10 border border-rose-500/30 rounded-xl px-4 py-3">{error}</p>}
          <button onClick={start} disabled={busy || url.trim().length < 10}
            className="w-full py-3.5 rounded-2xl font-bold bg-gradient-to-r from-sky-500 to-indigo-500 hover:opacity-90 disabled:opacity-40 transition flex items-center justify-center gap-2">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />} {audio ? 'Télécharger le MP3' : 'Télécharger en MP4'}
          </button>
          {status && <p className="text-xs text-gray-400 text-center">Gratuit · {left} / {limit} téléchargements restants aujourd'hui</p>}
        </>
      )}

      {running && (
        <div className="rounded-3xl bg-white/5 border border-white/10 p-6">
          <p className="text-sm mb-3 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> {job.status === 'queued' ? 'En attente d\'une place libre…' : `Téléchargement en cours… ${job.progress}%`}</p>
          <div className="h-2.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-gradient-to-r from-sky-500 to-indigo-500 transition-all duration-700" style={{ width: `${job.progress}%` }} /></div>
          <p className="text-xs text-gray-400 mt-4">Vous pouvez patienter ici, {audio ? 'le MP3' : 'la vidéo'} apparaîtra dès qu'il est prêt.</p>
        </div>
      )}

      {job?.status === 'done' && (
        <div className="space-y-4">
          <div className="rounded-3xl bg-white/5 border border-emerald-500/30 p-6 text-center">
            <CheckCircle2 className="w-10 h-10 text-emerald-300 mx-auto mb-2" />
            <p className="font-semibold break-all">{job.file_name}</p>
            <p className="text-xs text-blue-200 mt-1">{job.size_mb} Mo</p>
            <button onClick={save} className="mt-5 w-full py-3 rounded-2xl font-bold bg-gradient-to-r from-emerald-500 to-teal-500 hover:opacity-90 transition flex items-center justify-center gap-2">
              <Download className="w-5 h-5" /> {audio ? 'Enregistrer le MP3' : 'Enregistrer la vidéo'}
            </button>
          </div>
          {error && <p className="text-rose-300 text-sm">{error}</p>}
          <button onClick={reset} className="w-full py-3 rounded-2xl bg-white/10 hover:bg-white/20 transition font-semibold flex items-center justify-center gap-2"><RotateCcw className="w-4 h-4" /> {audio ? 'Télécharger un autre MP3' : 'Télécharger une autre vidéo'}</button>
          <p className="text-xs text-gray-400">Le fichier est supprimé du serveur après 6 heures.</p>
        </div>
      )}

      {job?.status === 'error' && (
        <div className="rounded-3xl bg-rose-500/10 border border-rose-500/30 p-6 text-center">
          <XCircle className="w-10 h-10 text-rose-300 mx-auto mb-2" />
          <p className="font-semibold mb-1">Téléchargement impossible</p>
          <p className="text-sm text-rose-200 mb-4">{job.error}</p>
          <button onClick={reset} className="px-5 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 transition inline-flex items-center gap-2"><RotateCcw className="w-4 h-4" /> Réessayer</button>
        </div>
      )}
    </div>
  );
}
